import { useEffect, useState, forwardRef } from 'react';
import { SingleValue } from 'react-select';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import { useTranslation } from 'react-i18next';

export interface CountryOption {
  value: string;
  label: string;
  flag: string;
}

interface CountrySelectProps {
  onChange: (selectedOption: SingleValue<CountryOption>) => void;
  defaultValue?: string;
  label?: string | React.ReactNode;
  error?: string;
}

const CountrySelect = forwardRef<HTMLDivElement, CountrySelectProps>(
  ({ onChange, defaultValue, label, error }, ref) => {
    const { t } = useTranslation();

    const [countries, setCountries] = useState<CountryOption[]>([]);
    const [selectedCountry, setSelectedCountry] =
      useState<SingleValue<CountryOption>>(null);

    useEffect(() => {
      fetch(
        'https://valid.layercode.workers.dev/list/countries?format=select&flags=true&value=code'
      )
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error: ${response.status}`);
          }
          return response.json();
        })
        .then(data => {
          setCountries(data.countries);
          if (defaultValue) {
            const defaultCountry = data.countries.find(
              (country: CountryOption) =>
                country.value.toLowerCase() === defaultValue.toLowerCase()
            );
            setSelectedCountry(defaultCountry || null);
          }
        })
        .catch(() => {
          // Silently fail - country list will remain empty
          // This is a non-critical feature, user can still proceed without country selection
        });
    }, [defaultValue]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChange = (selectedOption: any) => {
      const countryOption = selectedOption as SingleValue<CountryOption>;
      setSelectedCountry(countryOption);
      onChange(countryOption);
    };

    return (
      <SelectInput
        id="country"
        name="country"
        label={label}
        labelClass="!font-semibold pl-0.5"
        error={error}
        options={countries}
        value={selectedCountry}
        onChange={handleChange}
        ref={ref}
        placeholder={t('select')}
        isMulti={false}
        wrapperClass="w-full"
        className="text-[var(--in-fg)]"
      />
    );
  }
);

CountrySelect.displayName = 'CountrySelect';
export default CountrySelect;
